import { createSlice } from '@reduxjs/toolkit';
import { find, some, isEqual } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';
import axios from 'axios';

import socket from '../../utils/socket';

// URLSearchParams
const { demandId } = proc.getQueryParams();

const initialState = {
  isLoading: false,
  error: false,
  demandId,
  // datas
  demands: [],
  demand: {},
  // api call props
  getDemandsProps: null,
  // contractIds which have demand account in progress, pending on month
  contractIdsAccount: [],
  hasDemandChangeAccount: false // Modification informations en cours
};

const slice = createSlice({
  name: 'demands',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getDemands(state, action) {
      const { props } = action.payload;
      let { demands } = action.payload;
      demands = Array.isArray(demands) ? demands : [demands];

      state.isLoading = false;
      state.getDemandsProps = props;
      state.demands = demands;

      const { demandId } = proc.getQueryParams(state);
      const demand = find(state.demands, (el) => el.id === demandId);
      if (demand) {
        state.demand = demand;
        state.demandId = demandId;
      } else if (state.demands[0]) {
        const [demand] = state.demands;
        state.demand = demand;
        state.demandId = state.demand.id;
      }

      // Save all resource ids contracts which have an account
      // in progress, pending or accepted on month
      const contractIdsAccount = [];
      let hasDemandChangeAccount = false;

      demands.forEach((el) => {
        const isCurrentMonth = moment(el.datetime_created_at).format('YYYYMM') === moment().format('YYYYMM');
        const isDeposit = el.type_id === 3; // 3 = acompte
        const isValidStatus = ['En cours', 'En attente', 'Acceptée'].includes(el.status);
        const isAlreadyAdded = contractIdsAccount.includes(el.contract_id);
        if (isCurrentMonth && isDeposit && isValidStatus && !isAlreadyAdded) {
          contractIdsAccount.push(el.contract_id);
        }

        // Modification informations en cours ou en attente
        if (el.type_id === 1 && ['En cours', 'En attente'].includes(el.status)) {
          hasDemandChangeAccount = true;
        }
      });

      state.contractIdsAccount = contractIdsAccount.sort();
      state.hasDemandChangeAccount = hasDemandChangeAccount;
    },

    postDemand(state, action) {
      const { demand } = action.payload;
      state.isLoading = false;
      state.demands.unshift(demand);
      state.demand = demand;
      state.demandId = demand.id;

      // Modification informations en cours
      if (demand.type_id === 1) {
        state.hasDemandChangeAccount = true;
      }
      // New account demand
      if (demand.type_id === 3 && !state.contractIdsAccount.includes(demand.contract_id)) {
        state.contractIdsAccount.push(demand.contract_id);
        state.contractIdsAccount.sort();
      }
    },

    cancelDemand(state) {
      state.isLoading = false;
      const idx = state.demands.findIndex((el) => el.id === state.demand.id);
      state.demands.splice(idx, 1);

      // Modification informations en cours
      state.hasDemandChangeAccount = some(state.demands, { type_id: 1, status: 'En cours' });
      // Delete contractId account if cancel account in progress, pending on month
      if (
        moment(state.demand.datetime_created_at).format('YYYYMM') === moment().format('YYYYMM') &&
        state.demand.type_id === 3 &&
        ['En cours', 'En attente'].includes(state.demand.status)
      ) {
        const idx = state.contractIdsAccount.findIndex((contractId) => contractId === state.demand.contract_id);
        state.contractIdsAccount.splice(idx, 1);
        state.contractIdsAccount.sort();
      }

      // Sélectionne la première demande si elle existe
      state.demand = state.demands[0] || {};
      state.demandId = state.demand.id;
    },

    onSelectDemand(state, action) {
      state.demandId = parseInt(action.payload, 10);
      state.demand = find(state.demands, (el) => el.id === state.demandId);
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { onSelectDemand } = slice.actions;

export function getDemands(props, reload) {
  const { resourceId, clientUserId, demandId = null } = props;
  const path = `/demands${clientUserId ? '/partner' : ''}`;

  return async (dispatch, getState) => {
    const { getDemandsProps } = getState().demands;

    if (reload || !isEqual(props, getDemandsProps)) {
      dispatch(slice.actions.startLoading());
    }

    try {
      let result = null;
      if (clientUserId) {
        result = await socket.get(path, { clientUserId, demandId });
      } else {
        result = await socket.get(path, { resourceId, demandId });
      }

      dispatch(slice.actions.getDemands({ ...result, props }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function postDemand({ form, callback = null, isQuote = false, isPartner = false }) {
  return async (dispatch) => {
    const path = `/demands${isPartner ? '/partner' : ''}`;
    dispatch(slice.actions.startLoading());

    let body = null;
    const keysForm = Object.keys(form);
    body = new FormData();
    if (socket.user.userResourceId) {
      body.append('userResourceId', socket.user.userResourceId);
    } else if (socket.user.userClientId || isPartner) {
      body.append('userClientId', socket.user.userClientId);
    }

    keysForm.forEach((key) => {
      if (key === 'files') {
        let { files } = form;
        if (files && !Array.isArray(files)) {
          files = Array.from(files); // can be a fileslist object (ex : photo)
        }
        if (files?.length) {
          files.forEach((file) => body.append('files[]', file));
        }
      } else if (form[key] !== null && form[key] !== undefined) {
        body.append(key, form[key]);
      }
    });

    const headers = {
      Authorization: `Bearer ${socket.accessToken}`
    };
    await axios
      .post(`${socket.apiUrl}${path}`, body, { headers })
      .then((result) => {
        if (!result.data.success) {
          return callback(result.data);
        }
        if (!isQuote && !isPartner) {
          dispatch(slice.actions.postDemand(result.data));
        }
        callback();
      })
      .catch((err) => {
        callback(err);
        dispatch(slice.actions.hasError(err));
      });
  };
}

export function cancelDemand({ demandId, isPartner }, callback) {
  // TODO : call partner api route for partners demands cancellations
  const path = `/demands${isPartner ? '/partner' : ''}`;

  const demandCancelledStatus = 4;

  const commonProps = {
    datetime_treated_at: moment().format('YYYY-MM-DD HH:mm:ss'),
    status: demandCancelledStatus
  };

  const props = isPartner ? { ...commonProps, partnerDemandId: demandId } : { ...commonProps, id: demandId };

  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const request = isPartner ? socket.delete(path, props) : socket.put(path, props);
      const result = await request;
      dispatch(slice.actions.cancelDemand(result));

      callback(null, result);
    } catch (error) {
      callback(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
